import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    CurrentPersonDto,
    DO_NOTHING,
    EquipmentDto,
    EquipmentService,
    FEATURE_ORGANISATION_RELATIONSHIPS,
    FlyFreelyError,
    FlyFreelyLoggingService,
    hasFeatureFlag,
    PersonsOrganisationDto,
    PrintOption,
    toLocalDate,
    TransferResourceCommand,
    UserService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'equipment-view',
    templateUrl: './equipment-view.component.html'
})
export class EquipmentView implements OnInit, OnDestroy {
    @Input() equipment: EquipmentDto;
    @Input() organisation: PersonsOrganisationDto;
    @Input() allowEdit: boolean;
    @Output() edit = new EventEmitter<void>();
    @Output() deleted = new EventEmitter<void>();
    @Output() closeModal = new EventEmitter<void>();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    working = false;
    isSystemAdmin = false;

    // Does the container allow editing
    canUseNfc?: boolean;
    // Is editing available
    canEdit = true;
    canDelete?: boolean;
    isOwner?: boolean;

    canRetireEquipment = false;
    canMarkUnderMaintenance = false;
    canMarkServiceable = false;
    canMarkUnserviceable = false;
    canDisposeEquipment = false;
    hasCustodian = false;

    printOptions: PrintOption[] = [];

    constructor(
        private logging: FlyFreelyLoggingService,
        private commonDialoguesService: CommonDialoguesService,
        private equipmentService: EquipmentService,
        private userService: UserService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
        this.canUseNfc = false;
    }

    ngOnInit() {
        // this.refreshEquipment();
        this.refreshPermissions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('equipment' in changes) {
            this.refreshPermissions();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshPermissions() {
        if (!this.equipment) {
            this.canDelete = false;
            this.canEdit = false;
            this.isOwner = false;
            return;
        }
        this.canEdit = this.allowEdit;
        this.isOwner = this.equipment.organisationId === this.organisation.id;
        this.canRetireEquipment =
            this.equipment.status === EquipmentDto.Status.UNSERVICEABLE ||
            this.equipment.status === EquipmentDto.Status.SERVICEABLE ||
            this.equipment.status === EquipmentDto.Status.UNDER_MAINTENANCE;
        this.canMarkUnderMaintenance =
            this.equipment.status === EquipmentDto.Status.UNSERVICEABLE ||
            this.equipment.status === EquipmentDto.Status.RETIRED ||
            this.equipment.status === EquipmentDto.Status.SERVICEABLE;
        this.canMarkServiceable =
            this.equipment.status === EquipmentDto.Status.UNSERVICEABLE ||
            this.equipment.status === EquipmentDto.Status.UNDER_MAINTENANCE;
        this.canMarkUnserviceable =
            this.equipment.status === EquipmentDto.Status.SERVICEABLE;
        this.canDisposeEquipment =
            this.equipment.status !== EquipmentDto.Status.DISPOSED;

        this.hasCustodian = hasFeatureFlag(
            this.organisation,
            FEATURE_ORGANISATION_RELATIONSHIPS
        );

        this.isSystemAdmin =
            this.userService.getCurrentUser().type ===
            CurrentPersonDto.Type.SYSTEM_ADMIN;
    }

    deleteEquipment() {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete this equipment?',
                `Are you sure you wish to delete this equipment: ${this.equipment.name}`,
                'Delete',
                () =>
                    firstValueFrom(
                        this.equipmentService.delete(this.equipment.id)
                    )
            )
            .then(() => this.deleted.emit());
    }

    openTransferDialogue() {
        this.commonDialoguesService
            .showFormlyDialogue(
                'Transfer to another organisation',
                'Transfer',
                true,
                true,
                [
                    {
                        type: 'info',
                        props: {
                            message:
                                'Enter the organisation ID of the target organisation below. The equipment will be transferred to that organisation. This will unshare to any child organisations and transfer all maintenance to the new organisation.'
                        }
                    },
                    {
                        key: 'targetOrganisationId',
                        type: 'input',
                        props: {
                            type: 'number',
                            label: 'Target Organisation ID',
                            required: true
                        }
                    }
                ],
                {},
                (data: TransferResourceCommand) =>
                    firstValueFrom(
                        this.equipmentService.transfer(this.equipment.id, data)
                    )
            )
            .then(result => {
                this.logging.success('Equipment transfer successful');
                this.closeModal.emit();
            })
            .catch(DO_NOTHING);
    }

    editEquipment() {
        this.edit.emit();
    }

    retireEquipment() {
        this.equipmentService
            .retireEquipment(this.equipment.id, {
                managingOrganisationId: this.organisation.id
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: equipment => {
                    this.equipment = equipment;
                    this.logging.success(
                        `Equipment has been marked as retired`
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while marking equipment retired: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    markEquipmentUnderMaintenance() {
        this.equipmentService
            .markEquipmentUnderMaintenance(this.equipment.id, {
                managingOrganisationId: this.organisation.id
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: equipment => {
                    this.equipment = equipment;
                    this.logging.success(
                        `Equipment has been marked as under maintenance`
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while marking equipment under maintenance: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    markEquipmentServiceable() {
        this.equipmentService
            .markEquipmentServiceable(this.equipment.id, {
                managingOrganisationId: this.organisation.id
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: equipment => {
                    this.equipment = equipment;
                    this.logging.success(
                        `Equipment has been marked as serviceable`
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while marking equipment as serviceable: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    markEquipmentUnserviceable() {
        this.equipmentService
            .markEquipmentUnserviceable(this.equipment.id, {
                managingOrganisationId: this.organisation.id
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: equipment => {
                    this.equipment = equipment;
                    this.logging.success(
                        `Equipment has been marked as unserviceable`
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while marking equipment as unserviceable: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    disposeEquipment() {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showFormlyDialogue(
                'Dispose of Equipment',
                'Dispose',
                true,
                true,
                [
                    {
                        key: 'disposalDate',
                        type: 'date',
                        props: {
                            label: 'Disposal Date',
                            required: false
                        }
                    }
                ],
                { disposalDate: null as Date },
                data =>
                    firstValueFrom(
                        this.equipmentService.disposeEquipment(
                            this.equipment.id,
                            {
                                managingOrganisationId: this.organisation.id,
                                disposalDate: toLocalDate(data.disposalDate)
                            }
                        )
                    )
            )
            .then(equipment => {
                this.logging.success(`Equipment has been marked as disposed`);
                this.equipment = equipment;
                doneWorking();
            })
            .catch(() => doneWorking());
    }

    get hasManageOptions() {
        return (
            this.canRetireEquipment ||
            this.canMarkUnderMaintenance ||
            this.canMarkServiceable ||
            this.canMarkUnserviceable ||
            this.canDisposeEquipment
        );
    }
}
